import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject, PLATFORM_ID, RESPONSE_INIT } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { catchError, throwError, timeout } from 'rxjs';
import { HealthCheckService } from '../services/core/health-check.service';

/**
 * ERROR INTERCEPTOR
 * Best practices:
 *  - 401: preserve session until the auth interceptor attempts refresh
 *  - 403: log permission denial
 *  - 5xx: re-throw with user-friendly message
 *  - Network error: surface a connection message
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const healthService = inject(HealthCheckService);
  const server = isPlatformServer(inject(PLATFORM_ID));
  const response = inject(RESPONSE_INIT, { optional: true });

  const request = server ? next(req).pipe(timeout(8000)) : next(req);
  return request.pipe(
    catchError((error: HttpErrorResponse) => {
      if (server && response) {
        // An upstream outage must not become an indexable empty inventory page.
        response.status = error.status === 403 || error.status === 404 ? 404 : 503;
      }
      let userMessage = 'An unexpected error occurred. Please try again.';

      if (error.status === 401) {
        // AuthService owns refresh and logout. Clearing storage here would
        // destroy its refresh token before the outer auth interceptor runs.
        userMessage = req.url.includes('/Auth/login')
          ? error.error?.message || 'Invalid email or password.'
          : 'Session expired. Please log in again.';
      } else if (error.status === 403) {
        userMessage = 'You do not have permission to perform this action.';
        console.warn('[SegerYachts] 403 Forbidden:', req.url);
      } else if (error.status === 0 || error.status >= 500) {
        // Network / CORS or Server error — Trigger health check
        healthService.onApiError();
        userMessage =
          error.status === 0
            ? 'Cannot reach the server. Please check your connection.'
            : 'A server error occurred. Please try again later.';
        console.error(`[SegerYachts] API failure (${error.status}):`, req.url);
      } else if (error.status === 404) {
        userMessage = 'The requested resource was not found.';
      }

      // Re-throw with the enriched message so components can handle it
      return throwError(() => ({
        status: error.status,
        message: userMessage,
        originalError: error,
      }));
    }),
  );
};
